import React, { useState, useEffect, useRef } from "react";
import { useWebSocket } from "./WebSocketContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Button } from "@mui/material";

// TODO: Test this page for failure, http://localhost:3000/callback?code=<invalid code>&state=2
const CallbackOutlookApiPage = () => {
    const { t } = useTranslation();
    const { isConnected, sendMessage, subscribeToMessages } = useWebSocket();
    const navigate = useNavigate();
    const [caseId, setCaseId] = useState("");
    const [exportId, setExportId] = useState(null);
    const [datasourceId, setDatasourceId] = useState(null);
    const [connectorType, setConnectorType] = useState('');
    const [oauthProvider, setOauthProvider] = useState('');
    const [flowType, setFlowType] = useState('datasource'); // datasource | datasourceOAuth | action | pluginAction | pluginConnector | gmail | gmailAction | teamsAction | linkedinAction | onedrive | googledrive
    const [rawState, setRawState] = useState(null);
    const [code, setCode] = useState(null);
    const [result, setResult] = useState(null);
    const caseIdRef = useRef('');
    const exportIdRef = useRef(null);
    const datasourceIdRef = useRef(null);
    const connectorTypeRef = useRef('');
    const oauthProviderRef = useRef('');
    const flowTypeRef = useRef('datasource');
    const returnUrlRef = useRef(null);
    const exchangeSentRef = useRef(false);
    const exchangeCodeRef = useRef(null);

    useEffect(() => {
        console.log("CallbackOutlookApi mounted", { href: window.location.href });
        return () => {
            console.log("CallbackOutlookApi unmounted", { href: window.location.href });
        };
    }, []);

    useEffect(() => {
        caseIdRef.current = caseId;
    }, [caseId]);

    useEffect(() => {
        exportIdRef.current = exportId;
    }, [exportId]);

    useEffect(() => {
        datasourceIdRef.current = datasourceId;
    }, [datasourceId]);

    useEffect(() => {
        connectorTypeRef.current = connectorType;
    }, [connectorType]);

    useEffect(() => {
        oauthProviderRef.current = oauthProvider;
    }, [oauthProvider]);

    useEffect(() => {
        flowTypeRef.current = flowType;
    }, [flowType]);

    // Extract parameters from the URL on page load
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const codeFromUrl = params.get("code");
        const stateFromUrl = params.get("state");
        const oauthError = params.get("error");
        const oauthErrorDescription = params.get("error_description");

        setCode(codeFromUrl);
        setRawState(stateFromUrl);

        if (oauthError) {
            console.error("OAuth provider returned an error", {
                error: oauthError,
                description: oauthErrorDescription,
            });
            setResult(false);
        }

        const decodedPayload = stateFromUrl ? decodeOutlookState(stateFromUrl) : null;
        if (redirectCallbackToReturnUrlProtocol(decodedPayload?.returnUrl)) {
            return;
        }

        if (decodedPayload?.kind === "plugin_action_oauth") {
            if (decodedPayload?.caseId) {
                setCaseId(decodedPayload.caseId);
            }
            if (decodedPayload?.exportId) {
                setExportId(decodedPayload.exportId);
            }
            if (decodedPayload?.provider) {
                setOauthProvider(String(decodedPayload.provider));
            }
            if (decodedPayload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(decodedPayload.returnUrl);
            }
            setFlowType('pluginAction');
            console.log("Flow type set to: pluginAction, exportId:", decodedPayload?.exportId);
            return;
        }

        if (decodedPayload?.kind === "plugin_connector_oauth") {
            if (decodedPayload?.caseId) {
                setCaseId(decodedPayload.caseId);
            }
            if (decodedPayload?.datasourceId) {
                setDatasourceId(decodedPayload.datasourceId);
            }
            if (decodedPayload?.connectorType) {
                setConnectorType(String(decodedPayload.connectorType));
            }
            if (decodedPayload?.provider) {
                setOauthProvider(String(decodedPayload.provider));
            }
            if (decodedPayload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(decodedPayload.returnUrl);
            }
            setFlowType('pluginConnector');
            console.log("Flow type set to: pluginConnector, datasourceId:", decodedPayload?.datasourceId);
            return;
        }

        if (decodedPayload?.kind === "datasource_oauth") {
            if (decodedPayload?.caseId) {
                setCaseId(decodedPayload.caseId);
            }
            if (decodedPayload?.datasourceId) {
                setDatasourceId(decodedPayload.datasourceId);
            }
            if (decodedPayload?.connectorType) {
                setConnectorType(String(decodedPayload.connectorType));
            }
            if (decodedPayload?.provider) {
                setOauthProvider(String(decodedPayload.provider));
            }
            if (decodedPayload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(decodedPayload.returnUrl);
            }
            setFlowType('datasourceOAuth');
            console.log("Flow type set to: datasourceOAuth, datasourceId:", decodedPayload?.datasourceId);
            return;
        }

        if (stateFromUrl && stateFromUrl.startsWith("oa.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.exportId) {
                setExportId(payload.exportId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('action');
            console.log("Flow type set to: action, exportId:", payload?.exportId);
        } else if (stateFromUrl && stateFromUrl.startsWith("od.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.datasourceId) {
                setDatasourceId(payload.datasourceId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('datasource');
            console.log("Flow type set to: datasource");
        } else if (stateFromUrl && stateFromUrl.startsWith("odv.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.datasourceId) setDatasourceId(payload.datasourceId);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('onedrive');
            console.log("Flow type set to: onedrive");
        } else if (stateFromUrl && stateFromUrl.startsWith("gsa.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.exportId) {
                setExportId(payload.exportId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('gmailAction');
            console.log("Flow type set to: gmailAction, exportId:", payload?.exportId);
        } else if (stateFromUrl && stateFromUrl.startsWith("tsa.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.exportId) {
                setExportId(payload.exportId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('teamsAction');
            console.log("Flow type set to: teamsAction, exportId:", payload?.exportId);
        } else if (stateFromUrl && stateFromUrl.startsWith("lsa.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.exportId) {
                setExportId(payload.exportId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('linkedinAction');
            console.log("Flow type set to: linkedinAction, exportId:", payload?.exportId);
        } else if (stateFromUrl && stateFromUrl.startsWith("ga.")) {
            // Gmail authorization flow: state = "ga.<encoded payload>" (legacy: "ga.{caseId}")
            const payload = decodeOutlookState(stateFromUrl);
            const casePart = normalizeCallbackCaseId(payload?.caseId || stateFromUrl.substring(3));
            setCaseId(casePart);
            if (payload?.datasourceId) {
                setDatasourceId(payload.datasourceId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('gmail');
            setRawState(stateFromUrl);
            console.log("Flow type set to: gmail, caseId:", casePart, "datasourceId:", payload?.datasourceId);
        } else if (stateFromUrl && stateFromUrl.startsWith("gd.")) {
            const payload = decodeOutlookState(stateFromUrl);
            if (payload?.datasourceId) setDatasourceId(payload.datasourceId);
            if (payload?.caseId) {
                setCaseId(payload.caseId);
            }
            if (payload?.returnUrl) {
                returnUrlRef.current = sanitizeReturnUrl(payload.returnUrl);
            }
            setFlowType('googledrive');
            console.log("Flow type set to: googledrive");
        } else {
            setCaseId(normalizeCallbackCaseId(stateFromUrl));
            setFlowType('datasource');
            console.log("Flow type set to: datasource");
        }
    }, []); // Only run once on page load

    // Trigger exchangeAccessToken only when both code and caseId are available
    useEffect(() => {
        if (!code || !rawState || !isConnected) {
            return;
        }
        try {
            console.log("OAuth exchange effect fired", {
                code,
                state: rawState,
                flowType,
                href: window.location.href,
                hasOpener: !!window.opener
            });
        } catch (err) {
            // ignore logging errors
        }
        if (exchangeSentRef.current) {
            console.log("OAuth exchange skipped: already sent for this page load");
            return;
        }
        if (isExchangeCodeUsed(code)) {
            console.log("OAuth exchange skipped: code marked used", { code });
            handleDuplicateExchangeRedirect(navigate, flowTypeRef, returnUrlRef, caseIdRef, exportIdRef, datasourceIdRef, connectorTypeRef);
            return;
        }
        exchangeCodeRef.current = code;

        if (flowType === 'action') {
            if (caseId && exportId) {
                exchangeSentRef.current = true;
                markExchangeCodeUsed(code);
                console.log("Sending exchangeOutlookActionToken", { caseId, exportId });
                sendMessage({
                    command: "exchangeOutlookActionToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    exportId
                });
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'pluginAction') {
            if (caseId && exportId) {
                exchangeSentRef.current = true;
                markExchangeCodeUsed(code);
                console.log("Sending exchangePluginActionOAuthToken", { caseId, exportId, provider: oauthProvider });
                sendMessage({
                    command: "exchangePluginActionOAuthToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    exportId,
                    ...(oauthProvider ? { provider: oauthProvider } : {}),
                });
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'pluginConnector') {
            if (caseId && datasourceId && connectorType) {
                exchangeSentRef.current = true;
                markExchangeCodeUsed(code);
                console.log("Sending exchangePluginConnectorOAuthToken", { caseId, datasourceId, connectorType, provider: oauthProvider });
                sendMessage({
                    command: "exchangePluginConnectorOAuthToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    datasourceId,
                    type: connectorType,
                    ...(oauthProvider ? { provider: oauthProvider } : {}),
                });
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'datasourceOAuth') {
            if (caseId && datasourceId && connectorType) {
                exchangeSentRef.current = true;
                markExchangeCodeUsed(code);
                console.log("Sending exchangeDatasourceOAuthToken", { caseId, datasourceId, connectorType, provider: oauthProvider });
                sendMessage({
                    command: "exchangeDatasourceOAuthToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    datasourceId,
                    type: connectorType,
                    ...(oauthProvider ? { provider: oauthProvider } : {}),
                });
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'gmailAction') {
            if (caseId && exportId) {
                exchangeSentRef.current = true;
                markExchangeCodeUsed(code);
                console.log("Sending exchangeGmailActionToken", { caseId, exportId });
                sendMessage({
                    command: "exchangeGmailActionToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    exportId
                });
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'gmail') {
            if (caseId) {
                exchangeSentRef.current = true;
                markExchangeCodeUsed(code);
                console.log("Sending exchangeGmailToken", { caseId, datasourceId });
                sendMessage({
                    command: "exchangeGmailToken",
                    code,
                    state: rawState,
                    caseId,
                    ...(datasourceId ? { datasourceId } : {})
                });
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'onedrive') {
            if (caseId) {
                exchangeSentRef.current = true;
                markExchangeCodeUsed(code);
                console.log("Sending exchangeOneDriveToken", { caseId });
                sendMessage({ command: "exchangeOneDriveToken", code, caseId, datasourceId, state: rawState });
            } else {
                setResult(false);
            }
        } else if (flowType === 'googledrive') {
            if (caseId) {
                exchangeSentRef.current = true;
                markExchangeCodeUsed(code);
                console.log("Sending exchangeGoogleDriveToken", { caseId });
                sendMessage({ command: "exchangeGoogleDriveToken", code, caseId, datasourceId, state: rawState });
            } else {
                setResult(false);
            }
        } else if (flowType === 'teamsAction') {
            if (caseId && exportId) {
                exchangeSentRef.current = true;
                markExchangeCodeUsed(code);
                console.log("Sending exchangeTeamsActionToken", { caseId, exportId });
                sendMessage({
                    command: "exchangeTeamsActionToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    exportId
                });
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (flowType === 'linkedinAction') {
            if (caseId && exportId) {
                exchangeSentRef.current = true;
                markExchangeCodeUsed(code);
                console.log("Sending exchangeLinkedinActionToken", { caseId, exportId });
                sendMessage({
                    command: "exchangeLinkedinActionToken",
                    code,
                    state: rawState,
                    caseid: caseId,
                    exportId
                });
                setResult(true);
            } else {
                setResult(false);
            }
        } else if (caseId) {
            exchangeSentRef.current = true;
            markExchangeCodeUsed(code);
            console.log("Sending exchangeOutlookToken", { caseId, datasourceId });
            sendMessage({
                command: "exchangeOutlookToken",
                code,
                state: rawState,
                caseId,
                ...(datasourceId ? { datasourceId } : {})
            });
            setResult(true);
        } else {
            console.log("Code or caseId not found in URL, skipping token exchange...");
            setResult(false);
        }
    }, [isConnected, sendMessage, code, caseId, exportId, datasourceId, connectorType, oauthProvider, rawState, flowType, navigate]); // Re-run when dependencies change
    useEffect(() => {
        const unsubscribe = subscribeToMessages((parsedMessage) => {
            if (isOAuthTokenResultCommand(parsedMessage.command)) {
                markExchangeCodeUsed(exchangeCodeRef.current);
            }
            // Message is already parsed from WebSocketContext
            const handleDatasourceTokenResult = (provider, payload) => {
                const res = payload.result ?? payload.success ?? parsedMessage.result;
                const { error } = payload;
                setResult(res);
                const redirectPath = buildCaseRedirectPath(caseIdRef.current, payload.caseId);

                if (!res && error) {
                    console.error(`Error exchanging ${provider} token:`, error);
                }

                const returnUrl = returnUrlRef.current;
                if (res && returnUrl) {
                    if (flowTypeRef.current === 'datasource') {
                        navigateToReturnUrl(navigate, ensureDatasourceModal(returnUrl, "OutlookAPI"));
                    } else if (flowTypeRef.current === 'gmail') {
                        navigateToReturnUrl(navigate, ensureDatasourceModal(returnUrl, "Gmail"));
                    } else if (flowTypeRef.current === 'onedrive') {
                        navigateToReturnUrl(navigate, ensureDatasourceModal(returnUrl, "OneDrive"));
                    } else if (flowTypeRef.current === 'googledrive') {
                        navigateToReturnUrl(navigate, ensureDatasourceModal(returnUrl, "GoogleDrive"));
                    } else if (flowTypeRef.current === 'datasourceOAuth') {
                        const targetConnector = connectorTypeRef.current || payload.connectorType || payload.type || "OutlookAPI";
                        const targetDatasourceId = datasourceIdRef.current || payload.datasourceId || payload.datasource_id;
                        navigateToReturnUrl(navigate, ensureDatasourceModal(returnUrl, targetConnector, targetDatasourceId));
                    } else {
                        navigateToReturnUrl(navigate, returnUrl);
                    }
                    return;
                }

                if (provider === 'outlook') {
                    if (flowTypeRef.current === 'datasource') {
                        navigate(`${redirectPath}?openDatasource=true&connector=OutlookAPI`);
                    } else {
                        navigate(redirectPath);
                    }
                } else if (provider === 'gmail') {
                    navigate(`${redirectPath}?openDatasource=true&connector=Gmail`);
                } else if (provider === 'onedrive') {
                    navigate(`${redirectPath}?openDatasource=true&connector=OneDrive`);
                } else if (provider === 'googledrive') {
                    navigate(`${redirectPath}?openDatasource=true&connector=GoogleDrive`);
                } else if (provider === 'datasourceOAuth') {
                    const targetConnector = connectorTypeRef.current || payload.connectorType || payload.type || '';
                    const targetDatasourceId = datasourceIdRef.current || payload.datasourceId || payload.datasource_id;
                    if (targetConnector) {
                        navigate(buildDatasourceRedirectUrl(redirectPath, targetConnector, targetDatasourceId));
                    } else {
                        navigate(redirectPath);
                    }
                }
            };

            if (parsedMessage.command === "tokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const providerType = (payload.type || payload.provider || '').toString().toLowerCase();
                if (providerType === 'outlookapi') {
                    console.log("Received token result (Outlook):", parsedMessage);
                    handleDatasourceTokenResult('outlook', payload);
                } else if (providerType === 'gmail') {
                    console.log("Received token result (Gmail):", parsedMessage);
                    handleDatasourceTokenResult('gmail', payload);
                } else if (providerType === 'onedrive') {
                    console.log("Received token result (OneDrive):", parsedMessage);
                    handleDatasourceTokenResult('onedrive', payload);
                } else if (providerType === 'googledrive') {
                    console.log("Received token result (Google Drive):", parsedMessage);
                    handleDatasourceTokenResult('googledrive', payload);
                } else if (flowTypeRef.current === 'datasourceOAuth') {
                    console.log("Received token result (Datasource OAuth):", parsedMessage);
                    handleDatasourceTokenResult('datasourceOAuth', payload);
                }
                return;
            }

            if (parsedMessage.command === "pluginActionOAuthTokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const { verified, error } = payload;
                setResult(!!verified);
                if (!verified && error) {
                    console.error("Error exchanging plugin action token:", error);
                }
                const redirectPath = buildCaseRedirectPath(caseIdRef.current, payload.caseId);
                const targetExportId = exportIdRef.current || payload.exportId;
                const returnUrl = returnUrlRef.current;
                if (verified && returnUrl) {
                    navigateToReturnUrl(navigate, returnUrl);
                    return;
                }
                if ((flowTypeRef.current === 'pluginAction' || flowTypeRef.current === 'action') && targetExportId) {
                    navigate(`${redirectPath}?openActions=true&exportId=${targetExportId}`);
                    return;
                }
                navigate(redirectPath);
                return;
            }

            if (parsedMessage.command === "pluginConnectorOAuthTokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const { verified, error } = payload;
                setResult(!!verified);
                if (!verified && error) {
                    console.error("Error exchanging plugin connector token:", error);
                }
                const redirectPath = buildCaseRedirectPath(caseIdRef.current, payload.caseId);
                const targetConnectorType = connectorTypeRef.current || payload.type || payload.connectorType || '';
                const targetDatasourceId = datasourceIdRef.current || payload.datasourceId || payload.datasource_id;
                const returnUrl = returnUrlRef.current;
                if (verified && returnUrl) {
                    const sanitized = ensureDatasourceModal(returnUrl, targetConnectorType || "OutlookAPI", targetDatasourceId);
                    navigateToReturnUrl(navigate, sanitized);
                    return;
                }
                if (targetConnectorType) {
                    navigate(buildDatasourceRedirectUrl(redirectPath, targetConnectorType, targetDatasourceId));
                } else {
                    navigate(redirectPath);
                }
                return;
            }

            if (parsedMessage.command === "outlookTokenResult") {
                console.log("Received outlook token result:", parsedMessage);
                const payload = parsedMessage.data || parsedMessage;
                handleDatasourceTokenResult('outlook', payload);
            } else if (parsedMessage.command === "outlookActionTokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const { verified, error } = payload;
                setResult(!!verified);
                if (!verified && error) {
                    console.error("Error exchanging Outlook action token:", error);
                }
                const redirectPath = buildCaseRedirectPath(caseIdRef.current, payload.caseId);
                const targetExportId = exportIdRef.current;
                
                console.log("outlookActionTokenResult - flowType:", flowTypeRef.current, "exportId:", targetExportId);
                
                const returnUrl = returnUrlRef.current;
                if (verified && returnUrl) {
                    console.log("Navigating to returnUrl:", returnUrl);
                    navigateToReturnUrl(navigate, returnUrl);
                    return;
                }

                // Return to the DAG and reopen the specific action editor.
                if (flowTypeRef.current === 'action' && targetExportId) {
                    console.log("Navigating to DAG action editor with exportId:", targetExportId);
                    navigate(`${redirectPath}?openActions=true&exportId=${targetExportId}`);
                } else if (flowTypeRef.current === 'datasource') {
                    console.log("Navigating to Datasource modal (fallback)");
                    navigate(`${redirectPath}?openDatasource=true&connector=OutlookAPI`);
                } else {
                    console.log("Navigating without query params");
                    navigate(redirectPath);
                }
            } else if (parsedMessage.command === "gmailActionTokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const { verified, error } = payload;
                setResult(!!verified);
                if (!verified && error) {
                    console.error("Error exchanging Gmail action token:", error);
                }
                const redirectPath = buildCaseRedirectPath(caseIdRef.current, payload.caseId);
                const targetExportId = exportIdRef.current;

                const returnUrl = returnUrlRef.current;
                if (verified && returnUrl) {
                    navigateToReturnUrl(navigate, returnUrl);
                    return;
                }

                if (flowTypeRef.current === 'gmailAction' && targetExportId) {
                    navigate(`${redirectPath}?openActions=true&exportId=${targetExportId}`);
                } else {
                    navigate(redirectPath);
                }
            } else if (parsedMessage.command === "teamsActionTokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const { verified, error } = payload;
                setResult(!!verified);
                if (!verified && error) {
                    console.error("Error exchanging Teams action token:", error);
                }
                const redirectPath = buildCaseRedirectPath(caseIdRef.current, payload.caseId);
                const targetExportId = exportIdRef.current;

                const returnUrl = returnUrlRef.current;
                if (verified && returnUrl) {
                    navigateToReturnUrl(navigate, returnUrl);
                    return;
                }

                if (flowTypeRef.current === 'teamsAction' && targetExportId) {
                    navigate(`${redirectPath}?openActions=true&exportId=${targetExportId}`);
                } else {
                    navigate(redirectPath);
                }
            } else if (parsedMessage.command === "linkedinActionTokenResult") {
                const payload = parsedMessage.data || parsedMessage;
                const { verified, error } = payload;
                setResult(!!verified);
                if (!verified && error) {
                    console.error("Error exchanging LinkedIn action token:", error);
                }
                const redirectPath = buildCaseRedirectPath(caseIdRef.current, payload.caseId);
                const targetExportId = exportIdRef.current;

                const returnUrl = returnUrlRef.current;
                if (verified && returnUrl) {
                    navigateToReturnUrl(navigate, returnUrl);
                    return;
                }

                if (flowTypeRef.current === 'linkedinAction' && targetExportId) {
                    navigate(`${redirectPath}?openActions=true&exportId=${targetExportId}`);
                } else {
                    navigate(redirectPath);
                }
            } else if (parsedMessage.command === "gmailTokenResult") {
                console.log("Received gmail token result:", parsedMessage);
                const payload = parsedMessage.data || parsedMessage;
                handleDatasourceTokenResult('gmail', payload);
            } else if (parsedMessage.command === "oneDriveTokenResult") {
                console.log("Received onedrive token result:", parsedMessage);
                const payload = parsedMessage.data || parsedMessage;
                handleDatasourceTokenResult('onedrive', payload);
            } else if (parsedMessage.command === "googleDriveTokenResult") {
                console.log("Received googledrive token result:", parsedMessage);
                const payload = parsedMessage.data || parsedMessage;
                handleDatasourceTokenResult('googledrive', payload);
            }
        });

        // Cleanup on unmount
        return () => {
            unsubscribe();
        };
    }, [subscribeToMessages, navigate]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div style={{ textAlign: "center", marginTop: "2rem" }}>
                {/* we let case dashboard to notify the user about the result and next steps */}
                {(result === null || result === true) && <p>{t("outlook_api_callback.result_processing")}</p>}
                {result === false && (
                    <>
                        <p>{t("outlook_api_callback.result_error")}</p>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate(buildCaseRedirectPath(caseId))}
                        >
                            {t("outlook_api_callback.try_again")}
                        </Button>
                    </>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default CallbackOutlookApiPage;

function normalizeCallbackCaseId(value) {
    if (typeof value !== 'string' && typeof value !== 'number') {
        return '';
    }
    const normalized = String(value).trim();
    return /^[1-9]\d*$/.test(normalized) && Number.isSafeInteger(Number(normalized))
        ? normalized
        : '';
}

function buildCaseRedirectPath(caseId, fallbackCaseId) {
    const targetCaseId = normalizeCallbackCaseId(caseId) || normalizeCallbackCaseId(fallbackCaseId);
    return targetCaseId ? `/case/${targetCaseId}` : '/case';
}

function decodeOutlookState(stateValue) {
    try {
        const parts = stateValue.split(".");
        const encodedPayload = parts.length >= 2 ? parts[1] : stateValue;
        const normalized = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
        const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
        const decoded = window.atob(padded);
        const payload = JSON.parse(decoded);
        if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
            return null;
        }
        return { ...payload, caseId: normalizeCallbackCaseId(payload.caseId) };
    } catch (err) {
        console.error("Failed to decode Outlook state payload", err);
        return null;
    }
}

function redirectCallbackToReturnUrlProtocol(returnUrl) {
    if (!returnUrl || typeof returnUrl !== "string") {
        return false;
    }
    try {
        const current = new URL(window.location.href);
        const target = new URL(returnUrl);
        if (
            current.pathname === "/callback"
            && ["http:", "https:"].includes(target.protocol)
            && target.host === current.host
            && target.protocol !== current.protocol
            && target.protocol === "https:"
        ) {
            current.protocol = target.protocol;
            window.location.replace(current.toString());
            return true;
        }
    } catch (err) {
        return false;
    }
    return false;
}

function isLocalDevelopmentHost(hostname) {
    return hostname === "localhost"
        || hostname === "127.0.0.1"
        || hostname === "::1"
        || hostname === "[::1]"
        || hostname === "0.0.0.0";
}

function sanitizeReturnUrl(value) {
    if (!value || typeof value !== "string") {
        return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    if (trimmed.startsWith("//")) {
        return null;
    }

    try {
        const parsed = new URL(trimmed, window.location.origin);
        if (!isAllowedReturnUrl(parsed)) {
            return null;
        }
        const casePath = parsed.pathname.match(/^\/case\/([^/]+)/);
        if (casePath && !normalizeCallbackCaseId(decodeURIComponent(casePath[1]))) {
            return null;
        }
        const current = new URL(window.location.href);
        if (parsed.origin !== current.origin || parsed.protocol !== current.protocol) {
            return parsed.toString();
        }
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch (err) {
        return null;
    }
}

function ensureDatasourceModal(returnUrl, connector, datasourceId) {
    try {
        const url = new URL(returnUrl, window.location.origin);
        url.searchParams.set("openDatasource", "true");
        url.searchParams.set("connector", connector || "OutlookAPI");
        if (datasourceId) {
            url.searchParams.set("datasourceId", datasourceId);
        }
        if (url.origin !== window.location.origin && isAllowedReturnUrl(url)) {
            return url.toString();
        }
        return `${url.pathname}${url.search}${url.hash}`;
    } catch (err) {
        return returnUrl;
    }
}

function buildDatasourceRedirectUrl(redirectPath, connector, datasourceId) {
    const params = new URLSearchParams({
        openDatasource: "true",
        connector: connector || "OutlookAPI",
    });
    if (datasourceId) {
        params.set("datasourceId", datasourceId);
    }
    return `${redirectPath}?${params.toString()}`;
}

function isAllowedReturnUrl(url) {
    try {
        const current = new URL(window.location.href);
        if (!["http:", "https:"].includes(url.protocol)) {
            return false;
        }
        if (url.host === current.host) {
            return true;
        }
        return current.pathname === "/callback" && isLocalDevelopmentHost(url.hostname);
    } catch (err) {
        return false;
    }
}

function navigateToReturnUrl(navigate, returnUrl) {
    if (!returnUrl) {
        return;
    }
    try {
        const url = new URL(returnUrl, window.location.origin);
        const path = `${url.pathname}${url.search}${url.hash}`;
        if (url.origin === window.location.origin) {
            navigate(path);
            return;
        }
        if (isAllowedReturnUrl(url)) {
            window.location.assign(url.toString());
            return;
        }
    } catch (err) {
        if (typeof returnUrl === "string" && returnUrl.startsWith("/") && !returnUrl.startsWith("//")) {
            navigate(returnUrl);
        }
    }
}

function isExchangeCodeUsed(code) {
    if (!code || typeof window === "undefined") {
        return false;
    }
    try {
        const key = `oauth_code_used_${code}`;
        const raw = window.localStorage.getItem(key);
        if (!raw) {
            return false;
        }
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.ts) {
            return true;
        }
        if (Date.now() - parsed.ts > 5 * 60 * 1000) {
            window.localStorage.removeItem(key);
            return false;
        }
        return true;
    } catch (err) {
        return false;
    }
}

function markExchangeCodeUsed(code) {
    if (!code || typeof window === "undefined") {
        return;
    }
    try {
        const key = `oauth_code_used_${code}`;
        window.localStorage.setItem(key, JSON.stringify({ ts: Date.now() }));
    } catch (err) {
        // ignore storage errors
    }
}

function isOAuthTokenResultCommand(command) {
    return command === "tokenResult" || [
        "outlookTokenResult",
        "outlookActionTokenResult",
        "gmailTokenResult",
        "gmailActionTokenResult",
        "oneDriveTokenResult",
        "googleDriveTokenResult",
        "teamsActionTokenResult",
        "linkedinActionTokenResult",
        "pluginActionOAuthTokenResult",
        "pluginConnectorOAuthTokenResult",
    ].includes(command);
}

function handleDuplicateExchangeRedirect(
    navigate,
    flowTypeRef,
    returnUrlRef,
    caseIdRef,
    exportIdRef,
    datasourceIdRef,
    connectorTypeRef
) {
    const returnUrl = returnUrlRef.current;
    if (returnUrl) {
        if (flowTypeRef.current === 'datasource') {
            navigateToReturnUrl(navigate, ensureDatasourceModal(returnUrl, "OutlookAPI"));
            return;
        }
        if (flowTypeRef.current === 'gmail') {
            navigateToReturnUrl(navigate, ensureDatasourceModal(returnUrl, "Gmail"));
            return;
        }
        if (flowTypeRef.current === 'onedrive') {
            navigateToReturnUrl(navigate, ensureDatasourceModal(returnUrl, "OneDrive"));
            return;
        }
        if (flowTypeRef.current === 'googledrive') {
            navigateToReturnUrl(navigate, ensureDatasourceModal(returnUrl, "GoogleDrive"));
            return;
        }
        if (flowTypeRef.current === 'datasourceOAuth') {
            const connector = connectorTypeRef?.current || "OutlookAPI";
            navigateToReturnUrl(navigate, ensureDatasourceModal(returnUrl, connector, datasourceIdRef?.current));
            return;
        }
        if (flowTypeRef.current === 'pluginConnector') {
            const connector = connectorTypeRef?.current || "OutlookAPI";
            navigateToReturnUrl(navigate, ensureDatasourceModal(returnUrl, connector, datasourceIdRef?.current));
            return;
        }
        navigateToReturnUrl(navigate, returnUrl);
        return;
    }
    const redirectPath = buildCaseRedirectPath(caseIdRef.current);
    if ((flowTypeRef.current === 'action' || flowTypeRef.current === 'pluginAction' || flowTypeRef.current === 'gmailAction' || flowTypeRef.current === 'teamsAction' || flowTypeRef.current === 'linkedinAction') && exportIdRef.current) {
        navigate(`${redirectPath}?openActions=true&exportId=${exportIdRef.current}`);
        return;
    }
    if (flowTypeRef.current === 'datasource') {
        navigate(`${redirectPath}?openDatasource=true&connector=OutlookAPI`);
        return;
    }
    if (flowTypeRef.current === 'onedrive') {
        navigate(`${redirectPath}?openDatasource=true&connector=OneDrive`);
        return;
    }
    if (flowTypeRef.current === 'googledrive') {
        navigate(`${redirectPath}?openDatasource=true&connector=GoogleDrive`);
        return;
    }
    if (flowTypeRef.current === 'datasourceOAuth') {
        const connector = connectorTypeRef?.current || "OutlookAPI";
        navigate(buildDatasourceRedirectUrl(redirectPath, connector, datasourceIdRef?.current));
        return;
    }
    if (flowTypeRef.current === 'pluginConnector') {
        const connector = connectorTypeRef?.current || "OutlookAPI";
        navigate(buildDatasourceRedirectUrl(redirectPath, connector, datasourceIdRef?.current));
        return;
    }
    navigate(redirectPath);
}
